import { useEffect, useState } from "react";
import { Wallet, AlertTriangle, CircleCheck, Users, ChartBar, Hourglass } from "lucide-react";
import { fmtNum, fmtM, AGE } from "./echShared";

const MONO = { fontFamily: "'JetBrains Mono', Consolas, monospace" };

/** iter431 pt 11 — section à bande 40px : icône blanche 17px, titre 10px 500 majuscules dans la couleur claire. */
function Band({ color, light, icon: Icon, title, right, children, testId }) {
  return (
    <div className="flex" style={{ background: "#17171B", border: "1px solid #26262C", borderRadius: 12, overflow: "hidden" }} data-testid={testId}>
      <div className="flex items-center justify-center shrink-0" style={{ width: 40, background: color }}><Icon size={17} color="#fff" /></div>
      <div className="min-w-0" style={{ flex: 1, padding: "10px 12px" }}>
        <div className="flex items-center" style={{ gap: 8 }}><span style={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: ".1em", color: light, flex: 1 }}>{title}</span>{right}</div>
        {children}
      </div>
    </div>
  );
}

/** iter425 pt 12 / iter431 pt 10 — 4 chiffres clés (D3) : bande 28px + icône blanche 14px, valeur 16px mono. */
export function EchCards({ rows }) {
  const unpaid = rows.filter((r) => r.state !== "paid"), late = rows.filter((r) => r.state === "late"), paid = rows.filter((r) => r.state === "paid");
  const sum = (l, k) => l.reduce((a, r) => a + (Number(r[k]) || 0), 0);
  const encaisse = sum(rows, "paid"), echuNonPaye = sum(late, "remaining");
  const recouvrement = encaisse + echuNonPaye > 0 ? Math.round(encaisse / (encaisse + echuNonPaye) * 100) : 100;
  const clients = new Set(rows.map((r) => r.contact_id)).size, lateClients = new Set(late.map((r) => r.contact_id)).size;
  const cards = [
    ["a-encaisser", Wallet, "#3B9EFF", "À encaisser", fmtNum(sum(unpaid, "remaining")), "#E8E8EA", `DA · ${unpaid.length} échéance${unpaid.length > 1 ? "s" : ""}`],
    ["retard", AlertTriangle, "#EF4444", "En retard", fmtNum(echuNonPaye), "#F87171", `DA · ${late.length} échéance${late.length > 1 ? "s" : ""}`],
    ["encaisse", CircleCheck, "#22C55E", "Encaissé", fmtNum(encaisse), "#4ADE80", `DA · Recouvrement ${recouvrement} %`],
    ["clients", Users, "#5DCAA5", "Clients", String(clients), "#E8E8EA", `dont ${lateClients} en retard`],
  ];
  void paid;
  return (
    <div className="grid" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 }} data-testid="ech-cards">
      {cards.map(([k, Icon, c, title, value, vc, sub]) => (
        <div key={k} className="flex" style={{ background: "#17171B", border: "1px solid #26262C", borderRadius: 10, overflow: "hidden" }} data-testid={`ech-card-${k}`}>
          <span className="flex items-center justify-center shrink-0" style={{ width: 28, background: c }}><Icon size={14} color="#fff" /></span>
          <div style={{ padding: "8px 10px", minWidth: 0 }}>
            <div style={{ fontSize: 9.5, letterSpacing: ".06em", textTransform: "uppercase", color: "#8A8A93" }}>{title}</div>
            <div className="tabular-nums" style={{ ...MONO, fontSize: 16, fontWeight: 500, color: vc, marginTop: 2 }} data-testid={`ech-card-${k}-value`}>{value}</div>
            <div style={{ fontSize: 10, color: "#8A8A93" }}>{sub}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const monthKey = (d) => (d || "").slice(0, 7);
const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }).replace(".", "");

/** iter425 pt 13 — prévision par mois (modèle M3) : barres empilées Encaissé / Retard / À venir. */
export function EchForecast({ rows, f }) {
  const keys = (() => {
    const ks = new Set(rows.map((r) => monthKey(r.due_date)).filter(Boolean));
    if (f.date_from) ks.add(f.date_from.slice(0, 7)); if (f.date_to) ks.add(f.date_to.slice(0, 7));
    const arr = [...ks].sort(); if (!arr.length) return [];
    const out = []; let [y, m] = arr[0].split("-").map(Number); const [ly, lm] = arr[arr.length - 1].split("-").map(Number);
    while (y < ly || (y === ly && m <= lm)) { out.push(`${y}-${String(m).padStart(2, "0")}`); m += 1; if (m > 12) { m = 1; y += 1; } if (out.length > 36) break; }
    return out;
  })();
  const parts = [["paid", "Encaissé", "#22C55E", (r) => r.paid], ["late", "Retard", "#EF4444", (r) => (r.state === "late" ? r.remaining : 0)], ["upcoming", "À venir", "#3B9EFF", (r) => (r.state === "week" || r.state === "upcoming" ? r.remaining : 0)]];
  const data = keys.map((k) => { const rs = rows.filter((r) => monthKey(r.due_date) === k); const v = Object.fromEntries(parts.map(([p, , , fn]) => [p, rs.reduce((a, r) => a + (Number(fn(r)) || 0), 0)])); return { k, ...v, total: v.paid + v.late + v.upcoming }; });
  const max = Math.max(1, ...data.map((d) => d.total));
  return (
    <Band color="#3B6FD6" light="#93B4F5" icon={ChartBar} title="Prévision par mois" testId="ech-forecast"
      right={parts.map(([p, l, c]) => <span key={p} className="inline-flex items-center" style={{ gap: 4, fontSize: 10, color: "#8A8A93" }}><span style={{ width: 8, height: 8, borderRadius: 2, background: c }} />{l}</span>)}>
      {data.length === 0 ? <div style={{ padding: 18, textAlign: "center", fontSize: 11.5, color: "#6E6E78" }}>Aucune échéance sur la période.</div> : (
        <div className="grid" style={{ gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))`, gap: 6, marginTop: 10 }}>
          {data.map((d) => (
            <div key={d.k} className="flex flex-col items-center" data-testid={`ech-forecast-${d.k}`}>
              <div className="flex flex-col-reverse" style={{ height: 110, width: "100%", maxWidth: 34, borderRadius: 3, overflow: "hidden", justifyContent: "flex-start" }}>
                {parts.map(([p, , c]) => d[p] > 0 && <div key={p} style={{ height: `${d[p] / max * 100}%`, background: c, minHeight: 2 }} title={`${d.k} · ${p} · ${fmtNum(d[p])} DA`} />)}
              </div>
              <div style={{ fontSize: 10, color: "#C9C9D1", marginTop: 5, textTransform: "capitalize" }}>{monthLabel(d.k)}</div>
              {d.total === 0 ? <div style={{ fontSize: 10, color: "#4A4A52" }}>—</div> : parts.map(([p, , c]) => d[p] > 0 && <div key={p} className="tabular-nums" style={{ fontSize: 9.5, color: c, lineHeight: "13px" }}>{fmtM(d[p])}</div>)}
            </div>
          ))}
        </div>
      )}
    </Band>
  );
}

/** iter425 pt 14 — ancienneté des retards (modèle A3) : demi-jauge 3 zones, aiguille animée 1,2 s, 3 tuiles filtrantes. */
export function EchAging({ rows, lateRows, f, setF }) {
  const late = lateRows.filter((r) => r.state === "late");
  const avg = late.length ? Math.round(late.reduce((a, r) => a + r.days_late, 0) / late.length) : 0;
  const [angle, setAngle] = useState(-90);
  useEffect(() => { const t = setTimeout(() => setAngle(-90 + Math.min(180, avg) / 180 * 180), 50); return () => clearTimeout(t); }, [avg]);
  const totalLate = late.reduce((a, r) => a + r.remaining, 0);
  const arc = (from, to, color) => { const r = 70, cx = 90, cy = 86; const a0 = Math.PI + from / 180 * Math.PI, a1 = Math.PI + to / 180 * Math.PI; const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0), x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1); return <path d={`M ${x0} ${y0} A ${r} ${r} 0 0 1 ${x1} ${y1}`} stroke={color} strokeWidth={14} fill="none" strokeLinecap="butt" />; };
  void rows;
  return (
    <Band color="#E11D48" light="#FDA4AF" icon={Hourglass} title="Ancienneté des retards" testId="ech-aging">
      <div className="flex flex-col items-center" style={{ marginTop: 4 }}>
        <svg width={180} height={96} viewBox="0 0 180 96" data-testid="ech-aging-gauge">
          {arc(0, 30, "#F59E0B")}{arc(30, 90, "#EF4444")}{arc(90, 180, "#E11D48")}
          <g style={{ transform: `rotate(${angle}deg)`, transformOrigin: "90px 86px", transition: "transform 1.2s cubic-bezier(.2,.8,.2,1)" }} data-testid="ech-aging-needle" data-avg={avg}>
            <line x1={90} y1={86} x2={90} y2={26} stroke="#E8E8EA" strokeWidth={2.5} strokeLinecap="round" />
            <circle cx={90} cy={86} r={4} fill="#E8E8EA" />
          </g>
          <text x={14} y={94} fontSize={8} fill="#8A8A93">0</text><text x={154} y={94} fontSize={8} fill="#8A8A93">+180 j</text>
        </svg>
        <div style={{ fontSize: 12, fontWeight: 500, color: "#E8E8EA", marginTop: -6 }} data-testid="ech-aging-avg">Retard moyen {avg} j</div>
      </div>
      <div className="grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginTop: 10 }}>
        {AGE.map(([k, l, c, fn]) => { const rs = late.filter((r) => fn(r.days_late)); const amt = rs.reduce((a, r) => a + r.remaining, 0); const on = f.age === k; return (
          <button key={k} type="button" onClick={() => setF((x) => ({ ...x, age: on ? "" : k }))} className="text-left" style={{ background: "#1D1D23", borderRadius: 7, borderTop: `3px solid ${c}`, padding: "6px 8px", outline: on ? `1.5px solid ${c}` : "none", outlineOffset: 1 }} data-testid={`ech-age-${k}`} data-on={on}>
            <div style={{ fontSize: 10, color: "#8A8A93" }}>{l}</div>
            <div className="tabular-nums" style={{ fontSize: 13, fontWeight: 700, color: c }}>{fmtM(amt)}</div>
            <div className="tabular-nums" style={{ fontSize: 9.5, color: "#8A8A93" }}>{rs.length} éch. · {totalLate > 0 ? Math.round(amt / totalLate * 100) : 0} %</div>
          </button>); })}
      </div>
    </Band>
  );
}
